import type { AiPlugin } from '@/api/types';

// Shared derivations for a tool's "global reach", used by the catalog table,
// the tool detail drawer, and the per-agent link drawer so the three never
// disagree about what "global" means.
//
// A tool is GLOBAL when ai.AiPlugin.UserTypes is non-empty. The runtime's
// AIPluginToolProvider loads such a tool into EVERY agent of those caller
// types on every message, ignoring per-agent grants entirely. That is exactly
// the bypass an admin needs to see on this screen — a global tool cannot be
// trimmed from a single agent, only un-globaled for everyone.

/** ConversationUserType int → human label. 1 = Member (Patient), 2 = Merchant (Provider). */
export const AUDIENCE_LABELS: Record<number, string> = {
  1: 'Patient',
  2: 'Provider',
  3: 'Bot',
  4: 'Family',
  5: 'System',
};

/** True when the tool is force-loaded into every agent of at least one caller type. */
export function isGlobalTool(plugin: Pick<AiPlugin, 'userTypes'>): boolean {
  return (plugin.userTypes?.length ?? 0) > 0;
}

/** The caller types a global tool reaches, as labels (e.g. ["Patient", "Provider"]). */
export function audiencesOf(plugin: Pick<AiPlugin, 'userTypes'>): string[] {
  return (plugin.userTypes ?? []).map((t) => AUDIENCE_LABELS[t] ?? `Type ${t}`);
}

/** One-line reach summary for tooltips / detail rows. */
export function reachSummary(plugin: Pick<AiPlugin, 'userTypes'>): string {
  if (!isGlobalTool(plugin)) {
    return 'Agent-scoped — only agents explicitly granted this tool can call it.';
  }
  return `Global — loaded into every ${audiencesOf(plugin).join(' & ')} agent automatically, ignoring per-agent configuration.`;
}
